import { useParams, useNavigate } from 'react-router-dom'
import { useQuoteRequest, useAcceptQuote, useDeclineQuote, useApproveQuoteVisit, useRejectQuoteVisit } from '@/hooks/useQuoteRequests'
import Button from '@/components/common/Button/Button'
import Loading from '@/components/common/Loading/Loading'
import { toast } from 'react-hot-toast'
import { FaUser, FaMapMarkerAlt, FaCalendar, FaClock, FaDollarSign, FaFileAlt, FaUsers, FaCheckCircle, FaTimesCircle, FaExclamationCircle, FaTools, FaComments } from 'react-icons/fa'
import './QuoteDetail.css'

const QuoteDetail = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { data: quote, isLoading } = useQuoteRequest(Number(id))
    const acceptQuote = useAcceptQuote()
    const declineQuote = useDeclineQuote()
    const approveVisit = useApproveQuoteVisit()
    const rejectVisit = useRejectQuoteVisit()

    const handleAcceptQuote = async () => {
        if (!confirm('¿Estás seguro de que deseas aceptar esta cotización?')) return

        try {
            const result = await acceptQuote.mutateAsync({ id: Number(id), payload: {} })
            toast.success('Cotización aceptada. Por favor realiza el pago.')
            if (result.service_request_id) {
                navigate(`/request/${result.service_request_id}`)
            }
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Error al aceptar cotización')
        }
    }

    const handleDeclineQuote = async () => {
        if (!confirm('¿Estás seguro de que deseas rechazar esta cotización?')) return

        try {
            await declineQuote.mutateAsync({ id: Number(id), payload: {} })
            toast.success('Cotización rechazada')
            navigate('/requests')
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Error al rechazar cotización')
        }
    }

    const handleApproveVisit = async () => {
        try {
            await approveVisit.mutateAsync({ id: Number(id), payload: {} })
            toast.success('Visita aprobada. Por favor realiza el pago de la visita.')
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Error al aprobar visita')
        }
    }

    const handleRejectVisit = async () => {
        if (!confirm('¿Estás seguro de que deseas rechazar esta visita?')) return

        try {
            await rejectVisit.mutateAsync({ id: Number(id), payload: {} })
            toast.success('Visita rechazada')
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Error al rechazar visita')
        }
    }

    const getStatusColor = (status: string) => {
        const statusMap: Record<string, string> = {
            pending: '#F59E0B',
            sent: '#3B82F6',
            accepted: '#10B981',
            declined: '#EF4444',
            expired: '#6B7280',
            cancelled: '#EF4444',
        }
        return statusMap[status] || '#6B7280'
    }

    const getVisitStatusColor = (status: string) => {
        const statusMap: Record<string, string> = {
            none: '#6B7280',
            requested: '#F59E0B',
            approved: '#3B82F6',
            completed: '#10B981',
            rejected: '#EF4444',
        }
        return statusMap[status] || '#6B7280'
    }

    if (isLoading) return <Loading />

    if (!quote) {
        return (
            <div className="quote-detail-page-wrapper">
                <div className="quote-detail-container">
                    <div className="quote-detail-error">Cotización no encontrada</div>
                </div>
            </div>
        )
    }

    return (
        <div className="quote-detail-page-wrapper">
            <div className="quote-detail-container">
                <div className="quote-detail-header">
                    <Button variant="secondary" size="md" onClick={() => navigate(-1)}>
                        ← Volver
                    </Button>
                    <div className="quote-detail-header-info">
                        <h1>Cotización #{quote.id}</h1>
                        <div className="quote-detail-status-badge" style={{ backgroundColor: `${getStatusColor(quote.status)}20`, color: getStatusColor(quote.status) }}>
                            {quote.status_display}
                        </div>
                    </div>
                    {quote.has_conversation && quote.conversation_id && (
                        <Button 
                            variant="primary" 
                            size="md"
                            onClick={() => navigate(`/chat/${quote.conversation_id}`)}
                        >
                            <FaComments /> Chat
                        </Button>
                    )}
                </div>

                <div className="quote-detail-grid">
                    {/* Service Information Card */}
                    <div className="quote-detail-card quote-detail-main-card">
                        <h2 className="quote-detail-card-title">Información del Servicio</h2>
                        <div className="quote-detail-info-grid">
                            <div className="quote-detail-info-item">
                                <FaFileAlt className="quote-detail-info-icon" />
                                <div>
                                    <div className="quote-detail-info-label">Servicio</div>
                                    <div className="quote-detail-info-value">{quote.service_name}</div>
                                </div>
                            </div>
                            <div className="quote-detail-info-item">
                                <FaUser className="quote-detail-info-icon" />
                                <div>
                                    <div className="quote-detail-info-label">Proveedor</div>
                                    <div className="quote-detail-info-value">{quote.provider_name}</div>
                                </div>
                            </div>
                            <div className="quote-detail-info-item">
                                <FaMapMarkerAlt className="quote-detail-info-icon" />
                                <div>
                                    <div className="quote-detail-info-label">Distrito</div>
                                    <div className="quote-detail-info-value">{quote.district_details?.name || 'No especificado'}</div>
                                </div>
                            </div>
                        </div>
                        {quote.description && (
                            <div className="quote-detail-description">
                                <div className="quote-detail-description-label">Tu descripción:</div>
                                <p>{quote.description}</p>
                            </div>
                        )}
                    </div>

                    {/* Visit Status Card */}
                    {quote.visit_status !== 'none' && (
                        <div className="quote-detail-card">
                            <div className="quote-detail-card-header">
                                <h2 className="quote-detail-card-title">Estado de la Visita</h2>
                                <div className="quote-detail-status-badge" style={{ backgroundColor: `${getVisitStatusColor(quote.visit_status)}20`, color: getVisitStatusColor(quote.visit_status) }}>
                                    {quote.visit_status_display}
                                </div>
                            </div>
                            {quote.visit_datetime && (
                                <div className="quote-detail-info-item">
                                    <FaCalendar className="quote-detail-info-icon" />
                                    <div>
                                        <div className="quote-detail-info-label">Fecha y Hora</div>
                                        <div className="quote-detail-info-value">{new Date(quote.visit_datetime).toLocaleString('es-ES')}</div>
                                    </div>
                                </div>
                            )}
                            {quote.visit_cost && (
                                <div className="quote-detail-info-item">
                                    <FaDollarSign className="quote-detail-info-icon" />
                                    <div>
                                        <div className="quote-detail-info-label">Costo</div>
                                        <div className="quote-detail-info-value">S/ {quote.visit_cost}</div>
                                    </div>
                                </div>
                            )}
                            {quote.visit_notes && (
                                <div className="quote-detail-description">
                                    <div className="quote-detail-description-label">Notas:</div>
                                    <p>{quote.visit_notes}</p>
                                </div>
                            )}
                            
                            {/* Provider requested visit - Client can approve/reject */}
                            {quote.visit_status === 'requested' && quote.visit_requested_by === 'provider' && quote.can_requester_approve_visit && (
                                <div className="quote-detail-actions">
                                    <Button variant="success" size="lg" onClick={handleApproveVisit} disabled={approveVisit.isPending}>
                                        <FaCheckCircle /> {approveVisit.isPending ? 'Aprobando...' : 'Aprobar y Pagar Visita'}
                                    </Button>
                                    <Button variant="danger" size="lg" onClick={handleRejectVisit} disabled={rejectVisit.isPending}>
                                        <FaTimesCircle /> {rejectVisit.isPending ? 'Rechazando...' : 'Rechazar Visita'}
                                    </Button>
                                </div>
                            )}

                            {/* Visit payment pending */}
                            {quote.visit_payment_status === 'pending' && quote.visit_payment_request && (
                                <div className="quote-detail-action-card">
                                    <div className="quote-detail-action-description">
                                        <FaExclamationCircle style={{ marginRight: '0.5rem' }} />
                                        Debes realizar el pago de la visita antes de la fecha programada.
                                    </div>
                                    <Button variant="primary" size="lg" onClick={() => navigate(`/payment/${quote.visit_payment_request}`)}>
                                        Realizar Pago de Visita
                                    </Button>
                                </div>
                            )}

                            {/* Visit approved - waiting for completion */}
                            {quote.visit_status === 'approved' && quote.visit_payment_status === 'paid' && (
                                <div className="quote-detail-action-card">
                                    <div className="quote-detail-action-description">
                                        La visita ha sido aprobada y pagada. El proveedor realizará la visita en la fecha programada.
                                    </div>
                                </div>
                            )}

                            {/* Visit completed */}
                            {quote.visit_status === 'completed' && (
                                <div className="quote-detail-action-card">
                                    <div className="quote-detail-action-description" style={{ color: '#10B981' }}>
                                        <FaCheckCircle style={{ marginRight: '0.5rem' }} />
                                        La visita ha sido completada. El proveedor enviará la cotización pronto.
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Sent Quote Card */}
                    {quote.status === 'sent' && (
                        <div className="quote-detail-card">
                            <h2 className="quote-detail-card-title">Cotización Recibida</h2>
                            <div className="quote-detail-quote-summary">
                                <div className="quote-detail-quote-amount">
                                    <div className="quote-detail-quote-label">Total Cotizado</div>
                                    <div className="quote-detail-quote-value">S/ {quote.quoted_total}</div>
                                </div>
                                {quote.quoted_hours && (
                                    <div className="quote-detail-info-item">
                                        <FaClock className="quote-detail-info-icon" />
                                        <div>
                                            <div className="quote-detail-info-label">Horas estimadas</div>
                                            <div className="quote-detail-info-value">{quote.quoted_hours} horas</div>
                                        </div>
                                    </div>
                                )}
                                {quote.service_start_date && (
                                    <div className="quote-detail-info-item">
                                        <FaCalendar className="quote-detail-info-icon" />
                                        <div>
                                            <div className="quote-detail-info-label">Fecha de inicio</div>
                                            <div className="quote-detail-info-value">{new Date(quote.service_start_date).toLocaleString('es-ES')}</div>
                                        </div>
                                    </div>
                                )}
                                {quote.service_end_date && (
                                    <div className="quote-detail-info-item">
                                        <FaCalendar className="quote-detail-info-icon" />
                                        <div>
                                            <div className="quote-detail-info-label">Fecha de fin</div>
                                            <div className="quote-detail-info-value">{new Date(quote.service_end_date).toLocaleString('es-ES')}</div>
                                        </div>
                                    </div>
                                )}
                                {quote.provider_notes && (
                                    <div className="quote-detail-description">
                                        <div className="quote-detail-description-label">Notas del proveedor:</div>
                                        <p>{quote.provider_notes}</p>
                                    </div>
                                )}
                                {quote.quotation_participants && quote.quotation_participants.length > 0 && (
                                    <div className="quote-detail-participants-section">
                                        <div className="quote-detail-description-label">
                                            <FaUsers /> Participantes ({quote.quotation_participants.length})
                                        </div>
                                        <div className="quote-detail-participants-list">
                                            {quote.quotation_participants.map((p: any, idx: number) => (
                                                <div key={idx} className="quote-detail-participant-item">
                                                    <strong>{p.name}</strong>
                                                    <span>DNI: {p.dni}</span>
                                                    {/* TODO: CUL will be a PDF file upload in the future
                                                    {p.cul && <span>CUL: {p.cul}</span>}
                                                    */}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {quote.quotation_implements && quote.quotation_implements.length > 0 && (
                                    <div className="quote-detail-participants-section">
                                        <div className="quote-detail-description-label">
                                            <FaTools /> Herramientas/Implementos ({quote.quotation_implements.length})
                                        </div>
                                        <div className="quote-detail-participants-list">
                                            {quote.quotation_implements.map((i: any, idx: number) => (
                                                <div key={idx} className="quote-detail-participant-item">
                                                    <strong>{i.name}</strong>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            {quote.can_requester_accept_quote && (
                                <div className="quote-detail-actions">
                                    <Button variant="success" size="lg" onClick={handleAcceptQuote} disabled={acceptQuote.isPending}>
                                        <FaCheckCircle /> {acceptQuote.isPending ? 'Aceptando...' : 'Aceptar Cotización'}
                                    </Button>
                                    <Button variant="danger" size="lg" onClick={handleDeclineQuote} disabled={declineQuote.isPending}>
                                        <FaTimesCircle /> {declineQuote.isPending ? 'Rechazando...' : 'Rechazar Cotización'}
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Accepted Quote - Payment Info */}
                    {quote.status === 'accepted' && (
                        <div className="quote-detail-card">
                            <h2 className="quote-detail-card-title">Cotización Aceptada</h2>
                            <div className="quote-detail-action-card">
                                <div className="quote-detail-action-description" style={{ color: '#10B981' }}>
                                    <FaCheckCircle style={{ marginRight: '0.5rem' }} />
                                    Has aceptado esta cotización. Por favor realiza el pago del servicio para confirmar.
                                </div>
                                {quote.service_request_id && (
                                    <Button variant="primary" size="lg" onClick={() => navigate(`/request/${quote.service_request_id}`)}>
                                        Ver Detalles del Servicio
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Pending Status - Waiting for Provider */}
                    {quote.status === 'pending' && quote.visit_status === 'none' && (
                        <div className="quote-detail-card">
                            <h2 className="quote-detail-card-title">Solicitud Enviada</h2>
                            <div className="quote-detail-action-card">
                                <div className="quote-detail-action-description">
                                    Tu solicitud de cotización ha sido enviada al proveedor. Estamos esperando su respuesta.
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Declined Status */}
                    {quote.status === 'declined' && (
                        <div className="quote-detail-card">
                            <h2 className="quote-detail-card-title">Cotización Rechazada</h2>
                            <div className="quote-detail-action-card">
                                <div className="quote-detail-action-description" style={{ color: '#EF4444' }}>
                                    <FaTimesCircle style={{ marginRight: '0.5rem' }} />
                                    Has rechazado esta cotización.
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default QuoteDetail
